import { loadScript } from './util.js';

/**
 * Returns meta information from image file.
 */
const getImageMeta = async file => {
  if (!window.EXIF) {
    await loadScript('https://cdn.jsdelivr.net/npm/exif-js');
  }

  let name = file.name;

  let meta = await new Promise(resolve => {
    window.EXIF.getData(file, function getEXIFData() {
      resolve({
        // eslint-disable-next-line no-invalid-this
        datum: window.EXIF.getTag(this, 'GPSMapDatum'),
        // eslint-disable-next-line no-invalid-this
        latref: window.EXIF.getTag(this, 'GPSLatitudeRef'), // lat hemisphere
        // eslint-disable-next-line no-invalid-this
        lat: window.EXIF.getTag(this, 'GPSLatitude'),
        // eslint-disable-next-line no-invalid-this
        lngref: window.EXIF.getTag(this, 'GPSLongitudeRef'), // lng hemisphere
        // eslint-disable-next-line no-invalid-this
        lng: window.EXIF.getTag(this, 'GPSLongitude'),
        // eslint-disable-next-line no-invalid-this
        orientation: window.EXIF.getTag(this, 'Orientation'),
        // eslint-disable-next-line no-invalid-this
        date: window.EXIF.getTag(this, 'DateTime'),
        // eslint-disable-next-line no-invalid-this
        alltags: window.EXIF.getAllTags(this),
      });
    });
  });

  let alltags = meta.alltags;

  let date = meta.date;
  if (date) {
    date = date.replace('\0', ''); // trim trailing \0 character

    // Format: '2019:07:23 10:58:19'
    let match = date.match(/^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
    if (match) {
      let [, year, month, day, hours, minutes, seconds] = match;
      date = new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}`);
    } else {
      date = new Date(date);
    }
  }

  let orientation = meta.orientation;

  // If no datum, then assume WGS-84.
  if (
    (meta.datum && meta.datum != 'WGS-84') ||
    (meta.latref != 'N' && meta.latref != 'S') ||
    !meta.lat ||
    meta.lat.length != 3 ||
    (meta.lngref != 'W' && meta.lngref != 'E') ||
    !meta.lng ||
    meta.lng.length != 3
  ) {
    return {
      name,
      date,
      orientation,
      alltags,
    };
  }

  // Convert DDM (degrees decimal minutes) / DMS (degrees minutes seconds) to
  // DD (decimal degrees) coordinates.
  let lat_degrees = meta.lat[0];
  let lat_minutes = meta.lat[1];
  let lat_seconds = meta.lat[2];
  let lat = lat_degrees + lat_minutes / 60 + lat_seconds / 3600;
  if (meta.latref == 'S') {
    lat = -lat;
  }

  let lng_degrees = meta.lng[0];
  let lng_minutes = meta.lng[1];
  let lng_seconds = meta.lng[2];
  let lng = lng_degrees + lng_minutes / 60 + lng_seconds / 3600;
  if (meta.lngref == 'W') {
    lng = -lng;
  }

  return {
    name,
    lat,
    lng,
    date,
    orientation,
    alltags,
  };
};

/**
 * Returns data url for a blob.
 */
const toDataUrl = (blob, mimeType) =>
  mimeType
    ? URL.createObjectURL(blob, { type: mimeType })
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

/**
 * Orient image.
 */
const orientImage = (file, orientation) =>
  new Promise((resolve, reject) => {
    if (!orientation || orientation == 1) {
      resolve(file);
      return;
    }

    let img = new Image();
    img.crossOrigin = 'Anonymous';

    img.onload = async () => {
      let canvas = window.document.createElement('canvas');
      let { width, height } = img;

      if (orientation > 4) {
        canvas.height = width;
        canvas.width = height;
      } else {
        canvas.width = width;
        canvas.height = height;
      }

      let ctx = canvas.getContext('2d');

      ctx.save();

      switch (orientation) {
        case 2: // horizontal flip
          ctx.translate(width, 0);
          ctx.scale(-1, 1);
          break;
        case 3: // 180° rotate left
          ctx.translate(width, height);
          ctx.rotate(Math.PI);
          break;
        case 4: // vertical flip
          ctx.translate(0, height);
          ctx.scale(1, -1);
          break;
        case 5: // vertical flip + 90 rotate right
          ctx.rotate(0.5 * Math.PI);
          ctx.scale(1, -1);
          break;
        case 6: // 90° rotate right
          ctx.rotate(0.5 * Math.PI);
          ctx.translate(0, -height);
          break;
        case 7: // horizontal flip + 90 rotate right
          ctx.rotate(0.5 * Math.PI);
          ctx.translate(width, -height);
          ctx.scale(-1, 1);
          break;
        case 8: // 90° rotate left
          ctx.rotate(-0.5 * Math.PI);
          ctx.translate(-width, 0);
          break;
        default:
          break;
      }

      ctx.drawImage(img, 0, 0);
      ctx.restore();

      let blob = await new Promise(resolve =>
        canvas.toBlob(resolve, 'image/jpeg', 1)
      );
      resolve(blob);
    };
    img.onerror = e => reject(e);

    toDataUrl(file)
      .then(url => (img.src = url))
      .catch(reject);
  });

/**
 * Reduces image size and quality to fit the limit if needed.
 */
const fitImage = file =>
  new Promise((resolve, reject) => {
    const limit = 3e6; // 3mb
    if (file.size < limit) {
      resolve(file);
      return;
    }

    const maxWidth = 1920;

    let img = new Image();
    img.crossOrigin = 'Anonymous';

    img.onload = async () => {
      let canvas = document.createElement('canvas');
      canvas.width = Math.min(img.width, maxWidth);
      canvas.height = canvas.width * (img.height / img.width);

      let ctx = canvas.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      let blob = null;
      const qualities = [1, 0.5];
      for (let q of qualities) {
        blob = await new Promise(resolve =>
          canvas.toBlob(resolve, 'image/jpeg', q)
        );
        if (blob.size <= limit) {
          resolve(blob);
          return;
        }
      }
      reject(
        new Error(`Failed to reduce image size. Reduced size: ${blob.size}`)
      );
    };
    img.onerror = e => reject(e);

    toDataUrl(file)
      .then(url => (img.src = url))
      .catch(reject);
  });

const processImage = async file => {
  let meta = await getImageMeta(file);
  let blob = await orientImage(file, meta && meta.orientation);
  blob = await fitImage(blob);
  let url = await toDataUrl(blob);
  return {
    meta,
    url,
  };
};

export { getImageMeta, fitImage, orientImage, processImage, toDataUrl };
